/**
 * Centralized utility for managing versions across different data types (CV, Job Skills, and Company Research)
 * This provides consistent version handling for all data types in the application
 */

import { compareVersionItems } from './versionCompare';
import { createErrorLogger, ErrorType } from './errorHandler';

const logger = createErrorLogger('VersionManager');

/**
 * Data type configuration for different data types in the application
 * This defines the key properties and field mappings for each data type
 */
const DATA_TYPES = {
  cv: {
    itemsKey: 'competencies',
    itemIdentifier: 'competency',
    fieldsToCompare: ['proficiency', 'category', 'evidence'],
    requiredFields: ['competency', 'category', 'proficiency', 'evidence'],
    defaultValues: {
      category: 'General',
      proficiency: 'Intermediaire',
      evidence: ''
    }
  },
  job: {
    itemsKey: 'skills',
    itemIdentifier: 'name',
    fieldsToCompare: ['importance', 'category', 'context'],
    requiredFields: ['name', 'category', 'importance', 'context'],
    defaultValues: {
      category: 'General',
      importance: 'Required',
      context: ''
    }
  },
  company: {
    itemsKey: 'sections',
    itemIdentifier: 'name',
    fieldsToCompare: ['content'],
    requiredFields: ['name', 'content'],
    defaultValues: {
      content: ''
    }
  }
};

/**
 * Create a new version of data with proper metadata
 * @param {Object} currentData - The current data to create a version from
 * @param {Object} previousData - The previous version of the data (if any)
 * @param {String} type - The type of data ('cv', 'job', or 'company')
 * @param {String} author - The author of the version ('USER', 'AI', 'MANAGER', etc.)
 * @param {Object} additionalMetadata - Any additional metadata to include in the version
 * @returns {Object} The new version with metadata and comparison data
 */
export const createVersion = (currentData, previousData, type, author = 'System', additionalMetadata = {}) => {
  // Validate the data type
  if (!DATA_TYPES[type]) {
    logger.error('createVersion', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
    type = 'cv';
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];
  const itemsKey = typeConfig.itemsKey;

  // Create a standardized copy of the current data
  const standardizedCurrentData = standardizeItems(currentData, type);

  // Get the previous items for comparison (if available)
  const previousItems = previousData ? previousData[itemsKey] || [] : [];
  const currentItems = standardizedCurrentData[itemsKey] || [];

  // Create comparison data between previous and current items
  const comparisonData = compareVersionItems(
    currentItems,     // Current items should be first parameter
    previousItems,    // Previous items should be second parameter
    type              // Pass the type instead of itemsKey
  );

  // Calculate the new version number
  const previousVersionNumber = previousData?.versionNumber || 0;

  // Create the new version with metadata
  const newVersion = {
    ...standardizedCurrentData,
    timestamp: new Date().toISOString(),
    versionNumber: previousVersionNumber + 1,
    author: author,
    isModified: true,
    comparisonData,
    ...additionalMetadata
  };

  // Preserve type-specific fields from the original data
  // This ensures we don't lose any important metadata
  const preserveFields = {
    cv: ['name', 'position', 'company', 'education', 'experience', 'skills'],
    job: ['jobTitle', 'company', 'location', 'description', 'requirements'],
    company: ['companyName', 'industry', 'size', 'location', 'founded']
  };

  // Copy over any fields that should be preserved but might be missing in the standardized data
  preserveFields[type].forEach(field => {
    if (!newVersion[field] && currentData[field]) {
      newVersion[field] = currentData[field];
    }
  });

  return newVersion;
};

/**
 * Process versions to include comparison data
 * @param {Array} versions - Array of versions to process
 * @param {String} type - Type of data ('cv', 'job', or 'company')
 * @returns {Array} Processed versions with comparison data
 */
export const processVersions = (versions, type) => {
  if (!versions || versions.length === 0) return [];

  // Validate the data type
  if (!DATA_TYPES[type]) {
    logger.error('processVersions', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
    type = 'cv';
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];
  const itemsKey = typeConfig.itemsKey;

  // Sort versions by timestamp (newest first)
  const sorted = [...versions].sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  // Add comparison data for each version
  return sorted.map((version, index) => {
    // For the first version (newest), there's no previous version to compare with
    if (index === sorted.length - 1) {
      return version;
    }

    // For other versions, compare with the next version (which is older)
    const previousVersion = sorted[index + 1];

    // Create comparison data
    const comparisonData = compareVersionItems(
      version[itemsKey] || [],
      previousVersion[itemsKey] || [],
      type
    );

    // Return the version with comparison data
    return {
      ...version,
      comparisonData
    };
  });
};

/**
 * Safely prepare a version for viewing, ensuring all required fields are present
 * @param {Object} version - The version to prepare
 * @param {String} type - Type of data ('cv', 'job', or 'company')
 * @returns {Object} Version with all required fields
 */
export const prepareVersionForView = (version, type) => {
  if (!version) return null;

  // Validate the data type
  if (!DATA_TYPES[type]) {
    logger.error('prepareVersionForView', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
    type = 'cv';
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];
  const itemsKey = typeConfig.itemsKey;

  // Create a safe version with all required fields
  const safeVersion = {
    ...version,
    // Ensure items array exists
    [itemsKey]: version[itemsKey] || [],
    // Ensure other required fields exist with defaults
    timestamp: version.timestamp || new Date().toISOString(),
    versionNumber: version.versionNumber || 1,
    author: version.author || 'Unknown'
  };

  // Add type-specific fields with defaults
  if (type === 'cv') {
    safeVersion.name = version.name || '';
    safeVersion.position = version.position || '';
    safeVersion.company = version.company || '';
    safeVersion.education = version.education || '';
  } else if (type === 'job') {
    safeVersion.jobTitle = version.jobTitle || '';
    safeVersion.company = version.company || '';
    safeVersion.location = version.location || '';
  } else if (type === 'company') {
    safeVersion.companyName = version.companyName || '';
    safeVersion.industry = version.industry || '';
    safeVersion.size = version.size || '';
  }

  return safeVersion;
};

/**
 * Create comparison data for a version against a previous version
 * @param {Object} currentVersion - Current version
 * @param {Object} previousVersion - Previous version to compare against
 * @param {String} type - Type of data ('cv', 'job', or 'company')
 * @returns {Object} Current version with comparison data added
 */
export const createVersionComparison = (currentVersion, previousVersion, type) => {
  if (!currentVersion || !previousVersion) return currentVersion;

  // Validate the data type
  if (!DATA_TYPES[type]) {
    logger.error('createVersionComparison', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
    type = 'cv';
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];
  const itemsKey = typeConfig.itemsKey;

  // Create comparison data
  const comparisonData = compareVersionItems(
    currentVersion[itemsKey] || [],
    previousVersion[itemsKey] || [],
    type
  );

  // Return the version with comparison data
  return {
    ...currentVersion,
    previousVersion,
    comparisonData
  };
};

/**
 * Detect changes between two sets of items
 * @param {Array} currentItems - Current items
 * @param {Array} previousItems - Previous items
 * @param {String} type - Type of data ('cv', 'job', or 'company')
 * @returns {Boolean} True if changes are detected
 */
export const detectChanges = (currentItems, previousItems, type) => {
  if (!currentItems || !previousItems) return false;

  // Validate the data type
  if (!DATA_TYPES[type]) {
    logger.error('detectChanges', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
    type = 'cv';
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];
  // Removed unused itemIdentifier variable

  // Create string representations for comparison
  const stringifyItem = (item) => {
    const obj = {};
    typeConfig.requiredFields.forEach(field => {
      obj[field] = (item[field] || '').trim();
    });
    return JSON.stringify(obj);
  };

  // Convert items to string representations and sort
  const prevItemsStr = previousItems.map(stringifyItem).sort();
  const currItemsStr = currentItems.map(stringifyItem).sort();

  // Direct string comparison to detect any changes
  return JSON.stringify(prevItemsStr) !== JSON.stringify(currItemsStr);
};

/**
 * Save a new version to the version history
 * @param {Object} newVersion - The new version to save
 * @param {Array} existingVersions - The existing versions
 * @returns {Array} The updated version history with the new version
 */
export const saveVersion = (newVersion, existingVersions = []) => {
  // Mark all existing versions as inactive
  const updatedVersions = existingVersions.map(v => ({ ...v, isActive: false }));

  // Add the new version as active
  const versionToSave = {
    ...newVersion,
    isActive: true
  };

  // Return the updated version history with the new version at the beginning
  return [versionToSave, ...updatedVersions];
};

/**
 * Standardize an individual item (competency, skill, or section)
 * @param {Object} item - The item to standardize
 * @param {String} type - The type of data ('cv', 'job', or 'company')
 * @returns {Object} The standardized item
 */
export const standardizeItem = (item, type) => {
  if (!item) return null;

  // Validate the data type
  if (!DATA_TYPES[type]) {
    logger.error('standardizeItem', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
    type = 'cv';
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];

  // Create a copy of the item to avoid modifying the original
  const standardized = { ...item };

  // Handle special case for CV where competency might be stored as name
  if (type === 'cv' && !standardized.competency && standardized.name) {
    standardized.competency = standardized.name;
  }

  // Ensure all required fields are present with default values
  typeConfig.requiredFields.forEach(field => {
    // For CV, the main identifier might be either competency or name
    if (field === 'competency' && type === 'cv' && !standardized.competency) {
      standardized.competency = 'Unnamed Competency';
    }
    // For other types, use the default value if the field is missing
    else if (!standardized[field]) {
      standardized[field] = typeConfig.defaultValues[field] || '';
    }
  });

  return standardized;
};

/**
 * Standardize a collection of items (competencies, skills, or sections)
 * @param {Object} data - The data containing items to standardize
 * @param {String} type - The type of data ('cv', 'job', or 'company')
 * @returns {Object} The data with standardized items
 */
export const standardizeItems = (data, type) => {
  if (!data) return null;

  // Validate the data type
  if (!DATA_TYPES[type]) {
    logger.error('standardizeItems', `Invalid data type: ${type}. Using default type 'cv'.`, { type }, ErrorType.VALIDATION);
    type = 'cv';
  }

  // Get the configuration for this data type
  const typeConfig = DATA_TYPES[type];
  const itemsKey = typeConfig.itemsKey;

  // Create a copy of the data to avoid modifying the original
  const standardizedData = { ...data };

  // Ensure the items array exists
  if (!standardizedData[itemsKey]) {
    standardizedData[itemsKey] = [];
  } else if (!Array.isArray(standardizedData[itemsKey])) {
    // If it's not an array, convert it to an array
    standardizedData[itemsKey] = [standardizedData[itemsKey]];
  }

  // Standardize each item
  standardizedData[itemsKey] = standardizedData[itemsKey].map(item => standardizeItem(item, type));

  // Preserve existing timestamps - only add if truly missing
  // This prevents overwriting timestamps from existing version data
  if (!standardizedData.timestamp) {
    standardizedData.timestamp = new Date().toISOString();
  }

  return standardizedData;
};
